import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtMoney, HAUL_STATUSES, HAUL_TYPES, US_STATES, rpmColor } from "@/lib/theme";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const inputCls = "mt-1.5 h-11 border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute";
const labelCls = "font-display text-xs uppercase tracking-widest text-rig-dim";

const blankLoc = { label: "", city: "", state: "", zip: "", scheduled_at: "", ref_number: "", precise_notes: "" };

function blankForm() {
  return {
    broker: "",
    load_number: "",
    commodity: "",
    haul_type: "Dry Van",
    weight_lbs: "",
    rate_usd: "",
    miles: "",
    deadhead_miles: "",
    status: "booked",
    notes: "",
    pickup: { ...blankLoc },
    delivery: { ...blankLoc },
  };
}

function LocationSection({ title, loc, onChange }) {
  const set = (k, v) => onChange({ ...loc, [k]: v });
  return (
    <div className="rounded-2xl border border-rig-border bg-rig-surface p-5">
      <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-accent">{title}</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label className={labelCls}>Facility / Label</Label>
          <Input value={loc.label} onChange={(e) => set("label", e.target.value)} placeholder="Walmart DC #6094" className={inputCls} />
        </div>
        <div>
          <Label className={labelCls}>City</Label>
          <Input value={loc.city} onChange={(e) => set("city", e.target.value)} placeholder="Dallas" className={inputCls} />
        </div>
        <div>
          <Label className={labelCls}>State</Label>
          <Select value={loc.state || ""} onValueChange={(v) => set("state", v)}>
            <SelectTrigger className={inputCls}>
              <SelectValue placeholder="State" />
            </SelectTrigger>
            <SelectContent className="max-h-64 border-rig-border bg-rig-surface text-white">
              {US_STATES.map((s) => (
                <SelectItem key={s} value={s} className="focus:bg-rig-surface2">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={labelCls}>Appt / Scheduled</Label>
          <Input
            type="datetime-local"
            value={loc.scheduled_at || ""}
            onChange={(e) => set("scheduled_at", e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <Label className={labelCls}>Ref / PO #</Label>
          <Input value={loc.ref_number} onChange={(e) => set("ref_number", e.target.value)} placeholder="PO-12345" className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <Label className={labelCls}>Precise notes (dock #, gate code, height limit)</Label>
          <Textarea
            value={loc.precise_notes}
            onChange={(e) => set("precise_notes", e.target.value)}
            placeholder="Dock 14, gate code 4417, 13'6&quot; clearance"
            className="mt-1.5 min-h-[70px] border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
          />
        </div>
      </div>
    </div>
  );
}

export default function HaulEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [form, setForm] = useState(blankForm());
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const setTop = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const h = await api(`/hauls/${id}`);
      setForm({
        broker: h.broker || "",
        load_number: h.load_number || "",
        commodity: h.commodity || "",
        haul_type: h.haul_type || "Dry Van",
        weight_lbs: h.weight_lbs != null ? String(h.weight_lbs) : "",
        rate_usd: h.rate_usd != null ? String(h.rate_usd) : "",
        miles: h.miles != null ? String(h.miles) : "",
        deadhead_miles: h.deadhead_miles != null ? String(h.deadhead_miles) : "",
        status: h.status || "booked",
        notes: h.notes || "",
        pickup: { ...blankLoc, ...(h.pickup || {}) },
        delivery: { ...blankLoc, ...(h.delivery || {}) },
      });
    } catch (e) {
      toast.error(e?.message || "Couldn't load haul.");
      navigate("/hauls");
    } finally {
      setLoading(false);
    }
  }, [id, isNew, navigate]);

  useEffect(() => {
    document.title = isNew ? "New Haul · RigBooks" : "Haul · RigBooks";
    load();
  }, [load, isNew]);

  const rate = parseFloat(form.rate_usd) || 0;
  const miles = parseFloat(form.miles) || 0;
  const rpm = miles > 0 ? rate / miles : 0;

  const save = async () => {
    setSaving(true);
    const body = {
      broker: form.broker,
      load_number: form.load_number,
      commodity: form.commodity,
      haul_type: form.haul_type,
      weight_lbs: form.weight_lbs ? parseFloat(form.weight_lbs) : null,
      rate_usd: rate,
      miles,
      deadhead_miles: form.deadhead_miles ? parseFloat(form.deadhead_miles) : 0,
      status: form.status,
      notes: form.notes,
      pickup: form.pickup,
      delivery: form.delivery,
    };
    try {
      if (isNew) {
        await api("/hauls", { method: "POST", body });
        toast.success("Haul booked.");
      } else {
        await api(`/hauls/${id}`, { method: "PUT", body });
        toast.success("Haul updated.");
      }
      navigate("/hauls");
    } catch (e) {
      toast.error(e?.message || "Couldn't save haul.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api(`/hauls/${id}`, { method: "DELETE" });
      toast.success("Haul deleted.");
      navigate("/hauls");
    } catch (e) {
      toast.error(e?.message || "Delete failed.");
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-rig-accent" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" onClick={() => navigate("/hauls")} className="text-rig-dim hover:text-white">
          <ArrowLeft className="mr-2 h-4 w-4" /> All hauls
        </Button>
        {!isNew && (
          <Button
            variant="outline"
            onClick={() => setConfirmOpen(true)}
            data-testid="delete-haul-btn"
            className="border-rig-error/50 bg-transparent text-rig-error hover:bg-rig-error/10"
          >
            <Trash2 className="mr-2 h-4 w-4" /> Delete
          </Button>
        )}
      </div>

      {/* Live RPM card */}
      <div className="flex items-center justify-between rounded-2xl border-2 p-5" style={{ borderColor: rpmColor(rpm) }}>
        <div>
          <div className="font-display text-xs font-bold uppercase tracking-widest text-rig-dim">Rate Per Mile</div>
          <div className="mt-1 text-sm text-rig-dim">{fmtMoney(rate)} · {miles.toLocaleString()} mi</div>
        </div>
        <div className="font-display text-5xl font-bold" style={{ color: rpmColor(rpm) }} data-testid="haul-rpm-live">
          ${rpm.toFixed(2)}
        </div>
      </div>

      {/* Load details */}
      <div className="rounded-2xl border border-rig-border bg-rig-surface p-5">
        <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-[0.18em] text-rig-accent">Load Details</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className={labelCls}>Broker</Label>
            <Input value={form.broker} onChange={(e) => setTop("broker", e.target.value)} placeholder="TQL, Coyote…" data-testid="haul-broker" className={inputCls} />
          </div>
          <div>
            <Label className={labelCls}>Load #</Label>
            <Input value={form.load_number} onChange={(e) => setTop("load_number", e.target.value)} placeholder="123456" className={inputCls} />
          </div>
          <div>
            <Label className={labelCls}>Commodity</Label>
            <Input value={form.commodity} onChange={(e) => setTop("commodity", e.target.value)} placeholder="Dry goods" className={inputCls} />
          </div>
          <div>
            <Label className={labelCls}>Equipment</Label>
            <Select value={form.haul_type} onValueChange={(v) => setTop("haul_type", v)}>
              <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="border-rig-border bg-rig-surface text-white">
                {HAUL_TYPES.map((t) => (
                  <SelectItem key={t} value={t} className="focus:bg-rig-surface2">{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className={labelCls}>Rate (USD)</Label>
            <Input type="number" step="0.01" value={form.rate_usd} onChange={(e) => setTop("rate_usd", e.target.value)} placeholder="0.00" data-testid="haul-rate" className={inputCls} />
          </div>
          <div>
            <Label className={labelCls}>Loaded Miles</Label>
            <Input type="number" step="1" value={form.miles} onChange={(e) => setTop("miles", e.target.value)} placeholder="0" data-testid="haul-miles" className={inputCls} />
          </div>
          <div>
            <Label className={labelCls}>Deadhead Miles</Label>
            <Input type="number" step="1" value={form.deadhead_miles} onChange={(e) => setTop("deadhead_miles", e.target.value)} placeholder="0" className={inputCls} />
          </div>
          <div>
            <Label className={labelCls}>Weight (lbs)</Label>
            <Input type="number" step="1" value={form.weight_lbs} onChange={(e) => setTop("weight_lbs", e.target.value)} placeholder="0" className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <Label className={labelCls}>Status</Label>
            <Select value={form.status} onValueChange={(v) => setTop("status", v)}>
              <SelectTrigger data-testid="haul-status" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="border-rig-border bg-rig-surface text-white">
                {HAUL_STATUSES.map((s) => (
                  <SelectItem key={s.key} value={s.key} className="focus:bg-rig-surface2">{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <LocationSection title="Pickup" loc={form.pickup} onChange={(v) => setTop("pickup", v)} />
        <LocationSection title="Delivery" loc={form.delivery} onChange={(v) => setTop("delivery", v)} />
      </div>

      <div className="rounded-2xl border border-rig-border bg-rig-surface p-5">
        <Label className={labelCls}>Haul notes</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setTop("notes", e.target.value)}
          placeholder="Detention terms, lumper, special instructions…"
          className="mt-1.5 min-h-[80px] border-rig-borderStrong bg-rig-bg text-white placeholder:text-rig-mute"
        />
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={() => navigate("/hauls")} className="h-12 flex-1 border-rig-borderStrong bg-transparent text-white hover:bg-rig-surface2">
          Cancel
        </Button>
        <Button
          onClick={save}
          disabled={saving}
          data-testid="save-haul-btn"
          className="h-12 flex-[2] bg-rig-accent font-display text-sm font-bold uppercase tracking-widest text-rig-accentText hover:bg-rig-accentDark"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : isNew ? "Book Haul" : "Save Changes"}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete this haul?"
        description="This removes the load from your job tracker permanently."
        confirmLabel="Delete"
        destructive
        onConfirm={remove}
        testID="delete-haul-confirm"
      />
    </div>
  );
}
